import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { ApiResponse } from '@mb/common';
import { isBrowser } from '@mb/common/client';
import {
  TABLE_COLUMN_STORE,
  type HiddenColumnsByTable,
  type TableColumnStore,
} from '@mb/components';

const URL = '/api/v1/users/me/table-columns';

/**
 * The account's column preferences, fetched once per page.
 *
 * Every table asks for the whole set, and they all share this one promise:
 * a screen with two tables would otherwise send the same request twice at
 * the same moment, and a later one would race the first.
 *
 * Not refreshed after a save, because a save already knows its own answer
 * and the tables that did not take part are unaffected by it.
 */
@Injectable({ providedIn: 'root' })
export class HttpTableColumnStore implements TableColumnStore {
  readonly #http = inject(HttpClient);
  #pending: Promise<HiddenColumnsByTable> | null = null;

  load(): Promise<HiddenColumnsByTable> {
    // Nothing to ask on the server: there is no session there yet, and a
    // preference the first paint cannot know is better than a wrong one.
    if (!isBrowser()) return Promise.resolve({});

    this.#pending ??= firstValueFrom(
      this.#http.get<ApiResponse<HiddenColumnsByTable>>(URL),
    )
      .then((response) => response.data ?? {})
      .catch((error: unknown) => {
        // Forget the failure, so the next table to mount tries again
        // rather than inheriting it.
        this.#pending = null;
        throw error;
      });

    return this.#pending;
  }

  async save(table: string, hidden: readonly string[]): Promise<void> {
    const response = await firstValueFrom(
      this.#http.patch<ApiResponse<HiddenColumnsByTable>>(URL, {
        table,
        hidden: [...hidden],
      }),
    );
    // The server answers with the whole set as it now stands; keeping it
    // means a second table mounted later sees the save without asking.
    this.#pending = Promise.resolve(response.data ?? {});
  }
}

export function provideTableColumnStore() {
  return [{ provide: TABLE_COLUMN_STORE, useExisting: HttpTableColumnStore }];
}
